import React from 'react';
import styled from 'styled-components';
import Layout from 'layout';
import { graphql, useStaticQuery } from 'gatsby';
import Img from 'gatsby-image';
import { media } from 'theme/breakpoints';
import StartUsing from 'components/ui/startUsing';
import Opinion from 'components/compare/Opinion';
import TrustedBy from 'components/ui/trustedBy';
import CustomerBadge from 'components/customer/customerBadge';
import { Badge } from 'components/customer/customerBadge/styled';
import databoxBadge from 'img/databox/badge.png';
import Button, { StyledButton } from 'components/ui/button';
import Card from 'components/customer/card';
import { cards } from 'data/customers/cards';
import worldmap from 'img/worldmap.svg';

const StyledHeader = styled.header`
  display: flex;
  padding: 12rem 0 0;
  align-items: center;
  margin-bottom: 40px;
  ${media.desktop} {
    flex-direction: column;
    padding: 7.2rem 0 0;
  }
  ${media.tablet} {
    max-width: 576px;
    margin: 0 auto 90px;
  }
  ${media.mobile} {
    padding: 6.2rem 0 60px;
    margin: 0;
  }
  div {
    ${media.desktop} {
      text-align: center;
      ${StyledButton} {
        margin: 0 auto;
      }
    }
    ${media.tablet} {
      flex-basis: auto;
    }
    ${media.mobile} {
      text-align: left;
      ${StyledButton} {
        margin: 0;
      }
    }
    h1 {
      margin-bottom: 48px;
      ${media.tablet} {
        font-size: 28px;
        line-height: 38px;
      }
    }
  }
`;

const HeroImgBox = styled.div`
  position: relative;
  margin-left: 48px;
  flex-basis: 45%;
  ${media.desktop} {
    width: 90%;
    margin-top: 40px;
    margin-left: 0;
  }
  ${media.tablet} {
    width: 100%;
  }
  ${Badge} {
    top: 32px;
    left: 32px;
    ${media.tablet} {
      top: 16px;
      left: 16px;
    }
  }
`;

const StyledHeroImage = styled(Img)`
  flex-basis: 50%;
  position: relative;
  overflow: initial !important;
  min-width: 530px;
  &::before {
    content: '';
    position: absolute;
    bottom: -16px;
    right: -16px;
    background-color: #7dffef;
    width: 90%;
    height: 90%;
  }
  ${media.desktopWide} {
    min-width: 450px;
  }
  ${media.tablet} {
    flex-basis: auto;
  }
  ${media.mobile} {
    min-width: auto;
    &::before {
      bottom: -8px;
      right: -8px;
    }
  }
`;

export const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(1, 1fr);
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  ${media.tablet} {
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(3, auto);
    grid-row-gap: 32px;
  }
`;

const Section = styled.section`
  padding-top: 40px;
  ${media.tablet} {
    padding-top: 120px;
    padding-bottom: 0;
  }

  & h2,
  p.desc {
    text-align: center;
  }
  & p.desc {
    margin: 32px 0 64px;
    font-size: 22px;
    line-height: 34px;
  }
`;

const NumbersSection = styled.section`
  position: relative;
  margin: 40px 0;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: -35%;
    background-image: url(${worldmap});
    background-size: contain;
    background-position: right center;
    background-repeat: no-repeat;
    width: 100%;
    height: 100%;
    z-index: -1;
  }

  & p {
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 24px;
    & span {
      font-weight: 800;
    }
  }
  & div {
    margin-top: 48px;
    & a:not(:last-child) {
      margin-right: 56px;
    }
  }
  ${media.tablet} {
    margin: 30px 0;
    &::before {
      display: none;
    }
    & p {
      font-size: 26px;
      line-height: 36px;
      margin-bottom: 24px;
    }
    & div {
      display: grid;
      grid-template-columns: repeat(2, min-content);
      grid-template-rows: repeat(2, min-content);
      grid-column-gap: 48px;
      grid-row-gap: 32px;
      & a:not(:last-child) {
        margin-right: 0;
      }
    }
  }
  ${media.mobile} {
    & div a img {
      width: 90px !important;
    }
    & p {
      font-size: 22px;
      line-height: 30px;
      margin-bottom: 16px;
    }
  }
`;

const Customers = () => {
  const { databox } = useStaticQuery(graphql`
    query {
      databox: file(relativePath: { eq: "databox/banner.png" }) {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  return (
    <Layout
      metaTitle="Case Studies | Real Customer Stories"
      metaDescription="See how session replay software can boost your business. Read success stories and case studies from LiveSession’s customers."
      canonical="/customers/"
    >
      <div className="container">
        <StyledHeader>
          <div>
            <h1 className="line blue">
              How Databox achieved a 223% increase in conversions to paid accounts after switching
              to <span>LiveSession.</span>
            </h1>
            <Button href="/customers/databox/">Read story</Button>
          </div>
          <HeroImgBox>
            <StyledHeroImage
              fluid={databox.childImageSharp.fluid}
              fadeIn
              alt="Databox - hero"
              title="Databox - team"
            />
            <CustomerBadge img={databoxBadge} />
          </HeroImgBox>
        </StyledHeader>
        <TrustedBy customText="Trusted by" />
        <Section>
          <h2>LiveSession helps businesses of all sizes</h2>
          <p className="desc">Read our featured customer stories</p>
          <CardContainer>
            {cards.map((card) => (
              <Card key={card.link} {...card} />
            ))}
          </CardContainer>
        </Section>
        <NumbersSection>
          <p>
            <span>5,000,000</span> users analyzed every month
          </p>
          <p>
            <span>60+</span> countries
          </p>
          <p>
            <span>1000+</span> teams using LiveSession
          </p>
          <div>
            <a
              title="LiveSession is a leader in Session Replay on G2"
              href="https://www.g2.com/products/livesession/reviews?utm_source=rewards-badge"
            >
              <img
                style={{ width: 105 }}
                alt="LiveSession is a leader in Session Replay on G2"
                src="https://images.g2crowd.com/uploads/report_medal/image/1335/medal.svg"
              />
            </a>
            <a
              title="Users love LiveSession on G2"
              href="https://www.g2.com/products/livesession/reviews?utm_source=rewards-badge"
            >
              <img
                style={{ width: 105 }}
                alt="Users love LiveSession on G2"
                src="https://my.g2.com/assets/product-badges/users-love-us-2008b519df49af90dcfa7db4b5fe13c8ec24ced0348f0a6bd039711ad8bbffc7.svg"
              />
            </a>
            <a
              title="LiveSession is a leader in Session Replay on G2"
              href="https://www.g2.com/products/livesession/reviews?utm_source=rewards-badge"
            >
              <img
                style={{ width: 105 }}
                alt="LiveSession is a leader in Session Replay on G2"
                src="https://images.g2crowd.com/uploads/report_medal/image/1347/medal.svg"
              />
            </a>
            <a
              title="LiveSession is a leader in Europe Session Replay on G2"
              href="https://www.g2.com/products/livesession/reviews?utm_source=rewards-badge"
            >
              <img
                style={{ width: 105 }}
                alt="LiveSession is a leader in Europe Session Replay on G2"
                src="https://images.g2crowd.com/uploads/report_medal/image/1471/medal.svg"
              />
            </a>
          </div>
        </NumbersSection>
        <Opinion
          opinion="We had experimented with several solutions, but we weren’t fully satisfied with them. We needed to find the best-in-class session recording software. LiveSession lives up to our high expectations."
          companyID="databox"
          secondary
          style={{ paddingBottom: 48 }}
        />
      </div>
      <StartUsing />
    </Layout>
  );
};

export default Customers;
